import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { parseDocument } from "htmlparser2";
import { h } from "vue";

import DOMPurify from "isomorphic-dompurify";

import MoleculesPostH1 from "~/components/molecules/post/H1.vue";
import MoleculesPostH2 from "~/components/molecules/post/H2.vue";
import MoleculesPostH3 from "~/components/molecules/post/H3.vue";
import MoleculesPostH4 from "~/components/molecules/post/H4.vue";
import MoleculesPostH5 from "~/components/molecules/post/H5.vue";
import MoleculesPostH6 from "~/components/molecules/post/H6.vue";
import MoleculesPostBody from "~/components/molecules/post/Body.vue";
import MoleculesPostImage from "~/components/molecules/post/Image.vue";
import MoleculesPostUL from "~/components/molecules/post/UL.vue";
import MoleculesPostOL from "~/components/molecules/post/OL.vue";
import MoleculesPostQuote from "~/components/molecules/post/Quote.vue";
import MoleculesPostLink from "~/components/molecules/post/Link.vue";
import MoleculesPostTable from "~/components/molecules/post/Table.vue";

// --- Utility function built-in from shadcn ---
// Built for shadcn/ui projects to combine Tailwind classes safely.
// Internally uses `clsx` for conditional class handling and `twMerge` to resolve Tailwind conflicts.
export function cn ( ...inputs ) {
    return twMerge( clsx( inputs ) );
}

// Built for shadcn/ui projects to simplify ref updates.
// Accepts either a direct value or an updater function (like Vue's internal pattern).
export function valueUpdater ( updaterOrValue, ref ) {
    ref.value =
        typeof updaterOrValue === "function"
            ? updaterOrValue( ref.value )
            : updaterOrValue;
}

// Prevent empty arrays from rendering
export const shouldRender = ( content ) => {
    // If null or undefined, don't render
    if ( content === null || content === undefined ) return false;

    // For arrays, check length
    if ( Array.isArray( content ) ) return content.length > 0;

    // For strings, check length
    if ( typeof content === "string" ) return content.trim().length > 0;

    // For objects, check if not empty
    if ( typeof content === "object" ) return Object.keys( content ).length > 0;

    // For other types (boolean, number), return true if truthy
    return !!content;
};

export function renderLines ( texts ) {
    if ( !texts ) return "";
    if ( !Array.isArray( texts ) ) return texts;

    return texts.join( "\n" );
}

export function debounce ( fn, delay = 300 ) {
    let timeout = null;
    return ( ...args ) => {
        if ( timeout ) clearTimeout( timeout );
        timeout = setTimeout( () => {
            fn.apply( this, args ); // Ensure correct 'this' context
            timeout = null;
        }, delay );
    };
}

// --- WordPress Content Parser ---
export function renderWpContent ( html ) {
    if ( !html ) return "";

    // Replace WordPress shortcodes with valid HTML
    html = html
        .replace(
            /\[caption.*?\](.*?)\[\/caption\]/gs,
            function ( match, content ) {
                // Extract the image tag
                const imgTag = content.match( /<img[^>]*>/ );
                // Extract the caption text (usually after the image)
                const captionText = content.split( "/>" ).pop().trim();

                if ( imgTag ) {
                    // Create a special marker for our image with caption
                    return `<wp-image-with-caption>${ imgTag[ 0 ] }<wp-caption>${ captionText }</wp-caption></wp-image-with-caption>`;
                }
                return content;
            },
        )
        .replace(
            /<figure[^>]*>([\s\S]*?)<img([^>]*?)>([\s\S]*?)<figcaption[^>]*>([\s\S]*?)<\/figcaption>([\s\S]*?)<\/figure>/g,
            function (
                match,
                beforeImg,
                imgAttribs,
                afterImg,
                captionText,
                afterCaption,
            ) {
                // Create a consistent structure with the same format as your [caption] processing
                return `<wp-image-with-caption><img${ imgAttribs }><wp-caption>${ captionText.trim() }</wp-caption></wp-image-with-caption>`;
            },
        )
        // Handle figures without captions too
        .replace(
            /<figure[^>]*>([\s\S]*?)<img([^>]*?)>([\s\S]*?)<\/figure>/g,
            function ( match, beforeImg, imgAttribs, afterImg ) {
                if ( !match.includes( "<figcaption" ) ) {
                    return `<wp-image-with-caption><img${ imgAttribs }></wp-image-with-caption>`;
                }
                return match; // Leave it for the other regex to handle
            },
        )
        .replace( /[\u200B-\u200D\uFEFF]/g, "" )
        .replace( /<!--\s*(?:wp:[^>]*|[^>]*)-->/g, "" );

    const doc = parseDocument( html );

    function walk ( node ) {
        if ( !node ) return null;
        if (
            node.type === "text" ||
            node.type === "comment" ||
            node.type === undefined
        )
            return node.data;

        const tag = node.name;
        const children = node.children?.map( walk ).filter( Boolean );

        if ( children.length === 0 && tag !== "img" ) return null;

        switch ( tag ) {
            case "h1":
                return h( MoleculesPostH1, {}, { default: () => children } );
            case "h2":
                return h( MoleculesPostH2, {}, { default: () => children } );
            case "h3":
                return h( MoleculesPostH3, {}, { default: () => children } );
            case "h4":
                return h( MoleculesPostH4, {}, { default: () => children } );
            case "h5":
                return h( MoleculesPostH5, {}, { default: () => children } );
            case "h6":
                return h( MoleculesPostH6, {}, { default: () => children } );
            case "p":
                return h( MoleculesPostBody, {}, { default: () => children } );
            case "br":
                return;
            case "wp-image-with-caption": {
                // Find caption text
                const captionNode = node.children?.find(
                    ( child ) => child.name === "wp-caption",
                );
                const captionText = captionNode
                    ? captionNode.children?.[ 0 ]?.data || ""
                    : "";

                // Find image node
                const imgNode = node.children?.find(
                    ( child ) => child.name === "img",
                );

                if ( imgNode ) {
                    return h(
                        MoleculesPostImage,
                        {
                            image: {
                                src: imgNode.attribs?.src || "",
                                alt: imgNode.attribs?.alt || "",
                                width: imgNode.attribs?.width,
                                height: imgNode.attribs?.height,
                            },
                        },
                        { default: () => ( captionText ? [ captionText ] : [] ) },
                    ); // Pass caption as children
                }
                return { default: () => children };
            }
            case "img":
                return h(
                    MoleculesPostImage,
                    {
                        image: {
                            src: node.attribs?.src || "",
                            alt: node.attribs?.alt || "",
                            width: node.attribs?.width,
                            height: node.attribs?.height,
                            loading:
                                node.attribs?.loading === "lazy" ||
                                    node.attribs?.loading === "eager"
                                    ? node.attribs.loading
                                    : "lazy",
                            decoding:
                                node.attribs?.decoding === "async" ||
                                    node.attribs?.decoding === "auto" ||
                                    node.attribs?.decoding === "sync"
                                    ? node.attribs.decoding
                                    : "async",
                            srcset: node.attribs?.srcset || "",
                        },
                    },
                    { default: () => children },
                );
            case "ul":
                return h( MoleculesPostUL, {
                    items: children,
                } );
            case "ol":
                return h( MoleculesPostOL, {
                    items: children,
                } );
            case "blockquote":
                return h( MoleculesPostQuote, {}, { default: () => children } );
            case "a":
                // For links, preserve all attributes and children
                return h(
                    MoleculesPostLink,
                    {
                        href: node.attribs?.href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                    },
                    { default: () => children },
                );
            case "table":
                return h( MoleculesPostTable, {}, { default: () => children } );
            default:
                return h( tag, {}, { default: () => children } );
        }
    }

    return doc.children
        .map( walk )
        .filter( Boolean )
        .filter( ( child ) => child !== "\n" );
}

// Add this to your script section
export function formatDate ( dateString ) {
    if ( !dateString ) return "";

    const date = new Date( dateString );

    // Check if date is valid
    if ( isNaN( date.getTime() ) ) return "";

    // Format as YYYY.MM.DD
    const year = date.getFullYear();
    const month = String( date.getMonth() + 1 ).padStart( 2, "0" );
    const day = String( date.getDate() ).padStart( 2, "0" );

    return `${ year }.${ month }.${ day }`;
}

// --- Math and Random Utilities for 3D experience ---
// This section provides helper functions for generating random numbers,
// performing weighted selections, and common mathematical operations
// like linear interpolation (mix), range mapping (map), value clamping (clamp),
// and smooth interpolation (smoothstep).

export const random = ( ...args ) => {
    let min = 0;
    let max = 1;
    if ( args.length === 2 ) {
        [ min, max ] = args;
    } else if ( args.length === 1 ) {
        max = args[ 0 ];
    }
    return Math.random() * ( max - min ) + min;
};

export const chance = ( chance ) => {
    return Math.random() < chance;
};

export const randomSign = () => {
    return Math.sign( Math.random() - 0.5 );
};

export const weightedRandom = ( options ) => {
    const total = Object.values( options ).reduce( ( a, b ) => a + b, 0 );
    const rand = random( total );
    let sum = 0;
    for ( const [ key, value ] of Object.entries( options ) ) {
        sum += value;
        if ( rand < sum ) {
            return key;
        }
    }
    return null;
};

export const xx = ( ...args ) => {
    // return console.log(...args);
};

export const TO_RAD = Math.PI / 180;

export const mix = ( a, b, t, clampValues = true ) => {
    let _t = t;
    if ( clampValues ) {
        _t = clamp( t, 0, 1 );
    }
    return a * ( 1 - _t ) + b * _t;
};

export const map = ( v, a, b, c, d ) => {
    return c + ( ( d - c ) * ( v - a ) ) / ( b - a );
};

export const clamp = ( v, min, max ) => {
    return Math.min( Math.max( v, min ), max );
};

export const smoothstep = ( min, max, v ) => {
    v = clamp( ( v - min ) / ( max - min ), 0, 1 );
    return v * v * ( 3 - 2 * v );
};

export const isDev = process.env.NODE_ENV !== "production";

export const isTouchDevice =
    import.meta.client &&
    ( "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia( "(pointer: coarse)" ).matches );

// 避免 XSS 攻擊
export const htmlPurify = ( dirty ) => {
    return DOMPurify.sanitize( dirty, { ALLOWED_TAGS: [ 'br', 'span' ], ALLOWED_ATTR: [ 'class' ] } );
};

// 關鍵字反白
export const highlightText = ( text, keyword ) => {

    if ( !keyword || !text ) return text;

    const regex = new RegExp( `(${ keyword })`, "gi" );

    if ( Array.isArray( text ) ) {
        return text.map( ( item ) => {
            if ( !item ) return "";
            return item.replace(
                regex,
                '<span class="bg-primary-orange text-base-black">$1</span>',
            );
        } );
    } else {
        return text.replace(
            regex,
            '<span class="bg-primary-orange text-base-black">$1</span>',
        );
    }
};

export const getMainText = () => {
    const mainElement = document.querySelector( "main" );
    if ( !mainElement ) return "";

    // 創建一個深拷貝以避免修改原始 DOM
    const mainClone = mainElement.cloneNode( true );

    // 移除所有 script 和 style 標籤
    const scripts = mainClone.getElementsByTagName( "script" );
    const styles = mainClone.getElementsByTagName( "style" );

    while ( scripts.length > 0 ) {
        scripts[ 0 ].parentNode.removeChild( scripts[ 0 ] );
    }
    while ( styles.length > 0 ) {
        styles[ 0 ].parentNode.removeChild( styles[ 0 ] );
    }

    // 移除所有帶有 data-no-parse 屬性的元素
    const noParseElements = mainClone.querySelectorAll(
        "[data-no-parse='true']",
    );
    noParseElements.forEach( ( element ) => {
        element.parentNode.removeChild( element );
    } );

    // 獲取純文字內容
    return mainClone.textContent.trim();
};